import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import CurrencyIcon from './CurrencyIcon';

const Field = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => { navigator.clipboard.writeText(String(value)); setCopied(true); toast.success('Copié !'); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 border border-gray-200">
      <div className="min-w-0">
        <div className="text-[10px] uppercase font-bold text-gray-400">{label}</div>
        <div className="text-sm font-mono-data text-gray-900 truncate">{String(value)}</div>
      </div>
      <button onClick={copy} className="p-2 hover:bg-gray-100 rounded-full shrink-0 transition-colors">
        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
      </button>
    </div>
  );
};

export const AdminRequestDetail = ({ request, onClose, onRefresh }) => {
  const [giveAmount, setGiveAmount] = useState(String(request.give_amount ?? ''));
  const [receiveAmount, setReceiveAmount] = useState(String(request.receive_amount ?? ''));
  const [note, setNote] = useState(request.admin_note || '');
  const [busy, setBusy] = useState(false);

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      await api.put(`/admin/exchange-requests/${request.id}`, {
        give_amount: parseFloat(giveAmount), receive_amount: parseFloat(receiveAmount), admin_note: note, ...extra,
      });
      toast.success(extra.status ? `Statut : ${extra.status} ✓` : 'Modifications enregistrées ✓');
      onRefresh(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="request-detail-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-3xl max-w-xl w-full shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-black rounded-t-3xl p-6 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="text-white font-extrabold font-mono-data">Demande #{request.request_number}</div>
            <div className="text-white/50 text-xs mt-0.5">{request.email} · {new Date(request.created_at).toLocaleString('fr-FR')}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-testid="close-request-detail">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#F5F6F7] rounded-2xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                {request.give_method?.symbol && <CurrencyIcon symbol={request.give_method.symbol} size={22} />}
                <span className="text-xs font-bold text-gray-500 uppercase">Client donne ({request.give_method?.symbol || request.give_method?.name})</span>
              </div>
              <input type="number" step="any" value={giveAmount} onChange={(e) => setGiveAmount(e.target.value)}
                className="w-full bg-white rounded-xl px-4 py-2.5 outline-none font-mono-data border border-gray-200 focus:border-gray-900" data-testid="edit-give-amount" />
            </div>
            <div className="bg-[#F5F6F7] rounded-2xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                {request.receive_method?.symbol && <CurrencyIcon symbol={request.receive_method.symbol} size={22} />}
                <span className="text-xs font-bold text-gray-500 uppercase">Client reçoit ({request.receive_method?.symbol || request.receive_method?.name})</span>
              </div>
              <input type="number" step="any" value={receiveAmount} onChange={(e) => setReceiveAmount(e.target.value)}
                className="w-full bg-white rounded-xl px-4 py-2.5 outline-none font-mono-data border border-gray-200 focus:border-gray-900" data-testid="edit-receive-amount" />
            </div>
          </div>

          <div className="space-y-2">
            <Field label="Compte de réception client" value={request.receive_account_info} />
            <Field label="Infos compte d'envoi" value={request.give_account_info} />
            <Field label="Statut actuel" value={request.status} />
          </div>

          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note interne admin..."
            className="w-full bg-[#F5F6F7] rounded-xl px-4 py-2.5 outline-none text-sm resize-none" data-testid="request-note-input" />

          <div className="flex flex-wrap gap-2">
            <button onClick={() => save()} disabled={busy} className="btn-pill-dark px-5 py-2.5 text-sm flex items-center space-x-2 disabled:opacity-50" data-testid="save-request-btn">
              <Save className="w-4 h-4" /><span>Enregistrer</span>
            </button>
            {!['completed', 'cancelled', 'rejected'].includes(request.status) && (
              <>
                <button onClick={() => save({ status: 'completed' })} disabled={busy} className="bg-emerald-600 text-white rounded-full px-5 py-2.5 text-sm font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="request-complete-btn">Valider ✓</button>
                <button onClick={() => save({ status: 'rejected' })} disabled={busy} className="bg-rose-100 text-rose-700 rounded-full px-5 py-2.5 text-sm font-bold hover:bg-rose-200 transition-colors disabled:opacity-50" data-testid="request-reject-btn">Refuser</button>
                <button onClick={() => save({ status: 'cancelled' })} disabled={busy} className="border border-gray-300 text-gray-600 rounded-full px-5 py-2.5 text-sm font-bold hover:bg-gray-100 transition-colors disabled:opacity-50" data-testid="request-cancel-btn">Annuler</button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminRequestDetail;
